import { useState } from "react";
import type { Feed } from "@nebulize/sdk";

import { apiBaseUrl } from "@/lib/env";

import {
  Button,
  CopyField,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Page,
  Panel,
  Select,
  Stack,
  StatusBadge,
} from "@/components/ui";

import { FilterBar } from "../components/FilterBar";
import { Muted } from "../components/Text";

import { QueryPanel } from "./components/QueryPanel";
import {
  useChooseDiscordVoiceChannel,
  useDiscordVoiceChannels,
  useFeeds,
  useRegisterFeed,
  useSetFeedAuthorization,
  type RegisteredFeed,
} from "./lib/queries";
import { errorText, feedTone, stamp } from "./lib/present";

/** The header a sender presents the feed's secret in. Never a query parameter and never a path. */
const SECRET_HEADER = "X-Nebulize-Feed-Secret";

/** Where somebody else's server posts one arrival. Recoverable; the secret beside it is not. */
function deliveryAddress(feedId: string): string {
  return `${apiBaseUrl}/v1/feeds/${feedId}/deliveries`;
}

/**
 * The provider whose feeds the platform connects for you.
 *
 * A Discord feed has no sender to run and no secret to copy: the platform holds the voice
 * connection, so the row shows a room to pick rather than an address to paste.
 */
const DISCORD = "discord";

/** Where choosing Discord goes. A link, so no page ever holds the address. */
const DISCORD_INSTALL = `${apiBaseUrl}/v1/feeds/discord/install`;

/** The provider field's escape hatch. `FeedProvider` is open, so the list can never be the limit. */
const OTHER = "";

/**
 * The providers the console offers by name, and the one row that lets any other be typed.
 *
 * Naming two is an affordance, not a table the platform reasons from: a feed's provider is a flat
 * open identifier and `OTHER` is what keeps it open, so a provider nobody has built a screen for is
 * still one field away rather than a release away.
 */
const PROVIDERS = [
  { value: DISCORD, label: "Discord" },
  { value: "webhook", label: "Webhook" },
  { value: OTHER, label: "Other" },
];

/** The guild half of a Discord reference. `guild` alone means no room has been picked yet. */
function chosenChannel(reference: string | undefined): string | null {
  const [, channel] = (reference ?? "").split(":");
  return channel ? channel : null;
}

/** Pick the voice room a connected server listens in. */
function ChooseChannel({ feed, onClose }: { readonly feed: Feed; readonly onClose: () => void }) {
  const channels = useDiscordVoiceChannels(feed.feed_id);
  const choose = useChooseDiscordVoiceChannel();
  const [picked, setPicked] = useState(chosenChannel(feed.reference) ?? "");

  return (
    <Dialog open title={`Voice channel in ${feed.label || "this server"}`} onClose={onClose}>
      <Stack gap={4}>
        {channels.isPending ? (
          <Muted small>Reading the server.</Muted>
        ) : channels.isError ? (
          <StatusBadge tone="danger">{errorText(channels.error)}</StatusBadge>
        ) : (channels.data ?? []).length === 0 ? (
          <Muted small>That server has no voice channels the bot can see.</Muted>
        ) : (
          <Field label="Voice channel">
            {({ id }) => (
              <Select
                id={id}
                value={picked}
                options={[
                  { value: "", label: "Choose a channel" },
                  ...(channels.data ?? []).map((one) => ({
                    value: one.channel_id,
                    label: one.name,
                  })),
                ]}
                onChange={(event) => {
                  setPicked(event.target.value);
                }}
              />
            )}
          </Field>
        )}
        <div className="cx-verbs">
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={choose.isPending}
            disabled={picked === ""}
            onClick={() => {
              choose.mutate(
                { feedId: feed.feed_id, channelId: picked },
                { onSuccess: onClose },
              );
            }}
          >
            Use this channel
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
        <div aria-live="polite">
          {choose.isError ? (
            <StatusBadge tone="danger">{errorText(choose.error)}</StatusBadge>
          ) : null}
        </div>
      </Stack>
    </Dialog>
  );
}

/**
 * The one way a feed is added, whoever holds the connection.
 *
 * Discord is a feed like any other and is chosen here rather than behind a second button beside
 * this one: what differs is only who runs the sender, and that difference belongs inside the dialog
 * as a provider whose fields collapse, never as a separate verb a customer has to know to look for.
 */
function AddFeed({
  open,
  onClose,
  onRegistered,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onRegistered: (registered: RegisteredFeed) => void;
}) {
  const register = useRegisterFeed();
  const [chosen, setChosen] = useState<string>("webhook");
  const [typed, setTyped] = useState("");
  const [reference, setReference] = useState("");
  const [label, setLabel] = useState("");

  const provider = chosen === OTHER ? typed : chosen;
  const platformHeld = provider === DISCORD;

  return (
    <Dialog open={open} title="Add a feed" onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (platformHeld) {
            window.location.assign(DISCORD_INSTALL);
            return;
          }
          register.mutate(
            { provider: provider.trim(), reference: reference.trim(), label: label.trim() },
            {
              onSuccess: (registered) => {
                setReference("");
                setLabel("");
                onRegistered(registered);
                onClose();
              },
            },
          );
        }}
      >
        <Stack gap={4}>
          <Field label="Provider">
            {({ id }) => (
              <Select
                id={id}
                value={chosen}
                options={PROVIDERS}
                onChange={(event) => {
                  setChosen(event.target.value);
                }}
              />
            )}
          </Field>
          {chosen === OTHER ? (
            <Field label="Provider name">
              {({ id }) => (
                <Input
                  id={id}
                  mono
                  value={typed}
                  onChange={(event) => {
                    setTyped(event.target.value);
                  }}
                />
              )}
            </Field>
          ) : null}
          {platformHeld ? null : (
            <>
              <Field label="Reference">
                {({ id }) => (
                  <Input
                    id={id}
                    mono
                    value={reference}
                    onChange={(event) => {
                      setReference(event.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label="Label">
                {({ id }) => (
                  <Input
                    id={id}
                    value={label}
                    onChange={(event) => {
                      setLabel(event.target.value);
                    }}
                  />
                )}
              </Field>
            </>
          )}
          <div className="cx-verbs">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={register.isPending}
              disabled={provider.trim() === ""}
            >
              {platformHeld ? "Continue to Discord" : "Add"}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
          </div>
          <div aria-live="polite">
            {register.isError ? (
              <StatusBadge tone="danger">{errorText(register.error)}</StatusBadge>
            ) : null}
          </div>
        </Stack>
      </form>
    </Dialog>
  );
}

/** Every inbound connection this account holds, and the one switch that stops them all. */
export function ConsoleFeeds() {
  const feeds = useFeeds();
  const setAuthorization = useSetFeedAuthorization();
  const [adding, setAdding] = useState(false);
  const [minted, setMinted] = useState<RegisteredFeed | null>(null);
  const [choosing, setChoosing] = useState<Feed | null>(null);

  return (
    <Page
      title="Feeds"
      actions={
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            setAdding(true);
          }}
        >
          Add a feed
        </Button>
      }
    >

      {minted === null ? null : (
        <Panel
          title="Secret"
          action={
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setMinted(null);
              }}
            >
              Done
            </Button>
          }
        >
          <Stack gap={3}>
            <Muted small>{minted.feed.label || minted.feed.provider}</Muted>
            <CopyField value={minted.secret} label="feed secret" large />
            <Muted small>
              A sender posts arrivals to this address, presenting the secret in the{" "}
              <code>{SECRET_HEADER}</code> header. Never in the URL.
            </Muted>
            <CopyField
              value={deliveryAddress(minted.feed.feed_id)}
              label="delivery address"
              large
            />
          </Stack>
        </Panel>
      )}

      <QueryPanel title="Feeds" rows={4} query={feeds}>
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState
              headline="No feeds"
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setAdding(true);
                  }}
                >
                  Add a feed
                </Button>
              }
            />
          ) : (
            <FilterBar
              rows={rows}
              pageSize={12}
              text={(row) => `${row.label ?? ""} ${row.provider} ${row.reference ?? ""}`}
              facets={[
                { key: "provider", label: "Provider", values: (row) => [row.provider] },
                { key: "state", label: "State", values: (row) => [row.state ?? "pending"] },
              ]}
            >
              {(page) => (
                <DataTable
                  caption="Feeds held by this account"
                  density="comfortable"
                  columns={[
                    {
                      key: "label",
                      header: "Feed",
                      emphasis: true,
                      render: (row: Feed) => row.label || row.provider,
                    },
                    { key: "provider", header: "Provider", muted: true },
                    { key: "reference", header: "Reference", mono: true },
                    {
                      key: "delivery",
                      header: "Delivery address",
                      render: (row: Feed) =>
                        row.provider === DISCORD ? (
                          <Muted small>Held by Nebulize</Muted>
                        ) : (
                          <CopyField
                            value={deliveryAddress(row.feed_id)}
                            label={`delivery address for ${row.label || row.provider}`}
                          />
                        ),
                    },
                    {
                      key: "channel",
                      header: "Voice channel",
                      render: (row: Feed) =>
                        row.provider !== DISCORD ? null : (
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Choose a voice channel for ${row.label || row.provider}`}
                            onClick={() => {
                              setChoosing(row);
                            }}
                          >
                            {chosenChannel(row.reference) === null ? "Choose a channel" : "Change"}
                          </Button>
                        ),
                    },
                    {
                      key: "state",
                      header: "State",
                      render: (row: Feed) => (
                        <StatusBadge tone={feedTone(row.state)}>{row.state ?? "pending"}</StatusBadge>
                      ),
                    },
                    {
                      key: "created_at",
                      header: "Added",
                      muted: true,
                      render: (row: Feed) => stamp(row.created_at),
                    },
                    {
                      key: "authorization",
                      header: "",
                      align: "right",
                      width: "130px",
                      render: (row: Feed) =>
                        row.state === "revoked" ? null : row.state === "authorized" ? (
                          <Button
                            size="sm"
                            variant="destructive"
                            aria-label={`Revoke ${row.label || row.provider}`}
                            disabled={setAuthorization.isPending}
                            onClick={() => {
                              setAuthorization.mutate({
                                feedId: row.feed_id,
                                authorized: false,
                              });
                            }}
                          >
                            Revoke
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            aria-label={`Authorize ${row.label || row.provider}`}
                            disabled={setAuthorization.isPending}
                            onClick={() => {
                              setAuthorization.mutate({ feedId: row.feed_id, authorized: true });
                            }}
                          >
                            Authorize
                          </Button>
                        ),
                    },
                  ]}
                  rows={page}
                  emptyHeadline="No matches"
                />
              )}
            </FilterBar>
          )
        }
      </QueryPanel>

      <AddFeed
        open={adding}
        onClose={() => {
          setAdding(false);
        }}
        onRegistered={setMinted}
      />

      {choosing === null ? null : (
        <ChooseChannel
          feed={choosing}
          onClose={() => {
            setChoosing(null);
          }}
        />
      )}
    </Page>
  );
}
